import { ON_DEMAND_FILES, onDemand } from '../onDemand.js';
/**
 * The desktop app's rates for the page (the coordinator, 2026-10-22): its batched edits and its presence at most once a
 * second each, because each request the desktop sends costs two device signatures and shares the device's request limit
 * with the chat. One object, so the page and its header get the same rates. Unset elsewhere (the page's own rates).
 */
export const STARSCRIBE_DESKTOP_RATES = Object.freeze({ updatesMs: 1000, awarenessMs: 1000 });
/** The editor file the element loads, from the folder gyro-element.js came from. */
export const STARSCRIBE_EDITOR_FILE = 'gyro-starscribe.js';
/** What the person reads when the editor did not load. A browser keeps a failed file until the page reloads. */
export const STARSCRIBE_EDITOR_UNAVAILABLE = 'StarScribe could not load its page editor. Reload to try again.';
function isEditor(value) {
    if (!value || typeof value !== 'object')
        return false;
    const editor = value;
    return typeof editor.StarScribePage === 'function' && typeof editor.StarScribePageHeader === 'function';
}
/**
 * `load` run once: every later call gets the same promise. A load that failed is forgotten, so the next call tries again
 * (the person reopens the page).
 */
export function loadOnce(load) {
    let pending = null;
    return () => {
        if (pending)
            return pending;
        const attempt = load();
        pending = attempt;
        attempt.catch(() => { if (pending === attempt)
            pending = null; });
        return attempt;
    };
}
/**
 * Where the editor file is: the host's own name for it when it gives one (`override`: the desktop shell's
 * `window.__gyroStarscribeSrc`, version-stamped), read against the element's own script (`src`) and only on its origin;
 * else beside that script, with the element's query (the desktop's `?v=<content version>`, so a new release is never
 * served from the cache). Null when `src` is no URL.
 */
export function editorScriptUrl(elementScriptSrc, override) {
    if (!elementScriptSrc)
        return null;
    try {
        const element = new URL(elementScriptSrc);
        if (typeof override === 'string' && override.trim()) {
            const named = new URL(override.trim(), element);
            if (named.origin === element.origin && /\.js$/.test(named.pathname))
                return named.href;
        }
        const editor = new URL(STARSCRIBE_EDITOR_FILE, element);
        editor.search = element.search;
        return editor.href;
    }
    catch {
        return null;
    }
}
/**
 * The element's loader: one script tag for the editor file, after `shared` is on the window; it resolves with what the file
 * registered. A file that fails, or registers nothing usable, rejects with the plain sentence, and the console names it.
 */
export function scriptEditorLoader(options) {
    return loadOnce(() => new Promise((resolve, reject) => {
        const globals = options.window;
        const registered = () => globals.__gyroStarscribe;
        const ready = registered();
        if (isEditor(ready)) {
            resolve(ready);
            return;
        }
        globals.__gyroShared ??= options.shared();
        const script = options.document.createElement('script');
        script.src = options.url;
        script.async = true;
        script.dataset.gyroStarscribe = '';
        const fail = (why) => {
            script.remove();
            console.error(`[Gyro] ${STARSCRIBE_EDITOR_FILE} ${why}. The element loads it from its own folder (${options.url}) the first time a StarScribe page opens.`);
            reject(new Error(STARSCRIBE_EDITOR_UNAVAILABLE));
        };
        script.addEventListener('load', () => {
            const editor = registered();
            if (isEditor(editor))
                resolve(editor);
            else
                fail('loaded without registering the page editor');
        });
        script.addEventListener('error', () => fail('did not load'));
        options.document.head.appendChild(script);
    }));
}
let elementEditor = null;
let fromScript = null;
/**
 * Called by the element at boot (webcomponent.tsx) with its own script (document.currentScript, set only while the script
 * first runs) and the React it hands the editor file. From then on the editor comes from that file.
 */
export function bootStarscribeEditorScript(script, shared) {
    const src = script && typeof script.src === 'string' ? script.src : '';
    if (!editorScriptUrl(src))
        return;
    elementEditor = { src, shared };
    fromScript = null;
}
/** Every other build: the editor module, split into a file of its own by the bundler (vendor-pkg: gyro-react-starscribe.mjs). */
const fromModule = onDemand({
    file: ON_DEMAND_FILES.starscribe,
    what: 'StarScribe’s page editor',
    load: () => import('./editor.js'),
    check: isEditor,
    unavailable: STARSCRIBE_EDITOR_UNAVAILABLE,
});
let loadedEditor = null;
/** The page editor, loaded the first time a page opens: the element's file beside it, else the split module. */
export function loadStarscribePage() {
    let pending;
    const url = elementEditor && typeof document !== 'undefined' && typeof window !== 'undefined'
        ? editorScriptUrl(elementEditor.src, window.__gyroStarscribeSrc) : null;
    if (elementEditor && url) {
        fromScript ??= scriptEditorLoader({ document, window, url, shared: elementEditor.shared });
        pending = fromScript();
    }
    else {
        pending = fromModule();
    }
    pending.then((editor) => { loadedEditor = editor; }, () => undefined);
    return pending;
}
/** The editor when a load has finished already (a page opened before), so a page draws at once; null until then. */
export function loadedStarscribePage() {
    return loadedEditor;
}
//# sourceMappingURL=lazy.js.map